import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, RotateCcw, Upload } from 'lucide-react'
import type { ClientDraft, ColumnMap, FieldKey, ParsedTable } from './types'
import { FIELD_OPTIONS, autoMap, mapRows, missingRequired } from './parse'

const PREVIEW_LIMIT = 8
const MAX_IMPORT = 200

interface ImportReviewProps {
  table: ParsedTable
  onImport: (drafts: ClientDraft[]) => void
  onReset: () => void
  isSubmitting: boolean
}

export function ImportReview({ table, onImport, onReset, isSubmitting }: ImportReviewProps) {
  const [colMap, setColMap] = useState<ColumnMap>(() => autoMap(table.headers))
  const [skipInvalid, setSkipInvalid] = useState(true)

  const mapped = useMemo(() => mapRows(table, colMap), [table, colMap])
  const missing = useMemo(() => missingRequired(colMap), [colMap])

  const validRows = mapped.filter((r) => r.errors.length === 0)
  const invalidRows = mapped.filter((r) => r.errors.length > 0)

  const importable = skipInvalid ? validRows : mapped
  const canImport =
    missing.length === 0 &&
    importable.length > 0 &&
    (skipInvalid || invalidRows.length === 0) &&
    importable.length <= MAX_IMPORT

  const setColumn = (colIdx: number, key: FieldKey | null) => {
    setColMap((prev) => {
      const next = { ...prev, [colIdx]: key }
      // A destination field maps to at most one column — clear any prior owner.
      if (key) {
        for (const k of Object.keys(next)) {
          const idx = Number(k)
          if (idx !== colIdx && next[idx] === key) next[idx] = null
        }
      }
      return next
    })
  }

  const previewRows = mapped.slice(0, PREVIEW_LIMIT)

  return (
    <div className="intake-import-review">
      {/* Source summary */}
      <div className="intake-import-summary">
        <div className="intake-import-source">
          <span className="intake-import-source-name">{table.source}</span>
          <span className="intake-import-source-meta">
            {table.rows.length} row{table.rows.length === 1 ? '' : 's'} · {table.headers.length} columns
          </span>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>
          <RotateCcw size={14} /> Start over
        </button>
      </div>

      {/* Validation banners */}
      {missing.length > 0 && (
        <div className="intake-banner intake-banner--warn">
          <AlertTriangle size={15} />
          <span>
            Map a column to <strong>{missing.map((m) => m.label).join(', ')}</strong> to continue — it&apos;s
            required.
          </span>
        </div>
      )}
      {missing.length === 0 && (
        <div className="intake-import-stats">
          <span className="intake-stat intake-stat--ok">
            <CheckCircle2 size={14} /> {validRows.length} ready
          </span>
          {invalidRows.length > 0 && (
            <span className="intake-stat intake-stat--bad">
              <AlertTriangle size={14} /> {invalidRows.length} with issues
            </span>
          )}
        </div>
      )}

      {/* Mapping + preview table */}
      <div className="intake-table-scroll">
        <table className="intake-table">
          <thead>
            <tr>
              <th className="intake-table-rownum" aria-label="Row" />
              {table.headers.map((header, i) => (
                <th key={i}>
                  <div className="intake-col-header" title={header}>
                    {header || <span className="intake-optional">(blank)</span>}
                  </div>
                  <select
                    className="intake-col-select"
                    value={colMap[i] ?? ''}
                    onChange={(e) => setColumn(i, (e.target.value || null) as FieldKey | null)}
                  >
                    <option value="">— Ignore —</option>
                    {FIELD_OPTIONS.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, ri) => (
              <tr key={ri} className={row.errors.length ? 'intake-table-row--bad' : ''}>
                <td className="intake-table-rownum mono" title={row.errors.join('\n')}>
                  {row.errors.length ? <AlertTriangle size={12} /> : row.sourceRow}
                </td>
                {table.headers.map((_, ci) => (
                  <td key={ci} className={colMap[ci] ? '' : 'intake-cell--ignored'}>
                    {table.rows[ri]?.[ci] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mapped.length > PREVIEW_LIMIT && (
        <p className="intake-preview-more">
          Showing first {PREVIEW_LIMIT} of {mapped.length} rows.
        </p>
      )}

      {importable.length > MAX_IMPORT && (
        <div className="intake-banner intake-banner--warn">
          <AlertTriangle size={15} />
          <span>
            {importable.length} rows exceeds the {MAX_IMPORT}-row import limit. Split the file and import in
            batches.
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="intake-footer">
        <label className="intake-check">
          <input
            type="checkbox"
            checked={skipInvalid}
            onChange={(e) => setSkipInvalid(e.target.checked)}
            disabled={invalidRows.length === 0}
          />
          <span>
            Skip {invalidRows.length} row{invalidRows.length === 1 ? '' : 's'} with issues
          </span>
        </label>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canImport || isSubmitting}
          onClick={() => onImport(importable.map((r) => r.draft))}
        >
          <Upload size={14} />
          {isSubmitting ? 'Importing…' : `Import ${importable.length} client${importable.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}
